import api from '@/lib/services/general.service'
import { toast } from 'vue3-toastify';
import { toRaw } from 'vue'
import router from '@/router';
// The wording of every action notice, and what Undo on it does (busy-my-web#303).
import {
  noticeText,
  alreadyText,
  failedText,
  undoneText,
  undoableNotice,
  undoKindOf,
  endpointFor
} from '@/lib/emailActionNotice'

const _pendingTimelineFetches = new Map()

// localStorage helpers
const STORAGE_KEYS = {
  CONTACTS_FILTER: 'timeline_contacts_filter',
  TIMELINE_COUNTS: 'timeline_counts'
};

const storageHelper = {
  saveContactsFilter(data) {
    try {
      localStorage.setItem(STORAGE_KEYS.CONTACTS_FILTER, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save contacts filter to localStorage:', e);
    }
  },

  loadContactsFilter() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CONTACTS_FILTER);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.warn('Failed to load contacts filter from localStorage:', e);
      return null;
    }
  },

  saveTimelineCounts(data) {
    try {
      localStorage.setItem(STORAGE_KEYS.TIMELINE_COUNTS, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save timeline counts to localStorage:', e);
    }
  },

  loadTimelineCounts() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.TIMELINE_COUNTS);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.warn('Failed to load timeline counts from localStorage:', e);
      return null;
    }
  }
};

const initialState = (archived = false, deleted = false, important = false, waitingOn = false, overdue = false, folder = null) => {
  const cachedCounts = storageHelper.loadTimelineCounts();
  const cachedContacts = storageHelper.loadContactsFilter();

  return {
    timelines: {
      items: [],
      index: 0,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: 0,
      hasNextPage: 0,
    },
    requestData: {
      page: 1,
      pageSize: parseInt(localStorage.getItem('pageSize_inbox') || '50', 10),
      order: 1,
      search: '',
      archived: false,
      deleted: false,
      important: false,
      waitingOn: false,
      overdue: false,
      folder: null,
    },
    timelineCounts: cachedCounts || {
      totalCount: 0,
      unSeenCount: 0,
      waitingOnCount: 0,
      importantCount: 0,
      overdueCount: 0,
      binCount: 0,
      archiveCount: 0,
      archiveUnSeenCount: 0,
      sentCount: 0,
    },
    contactsFilter: cachedContacts || [],
    isLoading: true,
    isContactsFilterLoading: true,
    isAllEmailsSynced: false,
    contactsFilterLoadingError: null,
    timelineRefs: {},
    refreshIntervalId: null,
  };
};

export const state = initialState();

export const getters = {
  contactsFilter: s => s.contactsFilter.filter(contact => contact.totalCount > 0),
  contactsFilterFirstId: (state) => {
    const firstValidContact = state.contactsFilter.find(contact => contact.totalCount > 0);
    return firstValidContact ? firstValidContact.id : null;
  },
  contactsFilterNextId: (state) => (excludeId) => {
    const next = state.contactsFilter.find(c => c.totalCount > 0 && c.id !== excludeId);
    return next ? next.id : null;
  }, timelines: s => s.timelines,
  isLoading: s => s.isLoading,
  contactsFilterLoadingError: s => s.contactsFilterLoadingError,
  isAllEmailsSynced: s => s.isAllEmailsSynced,

  isContactsFilterLoading: s => s.isContactsFilterLoading,
  getTimelineById: (s) => (id) => {
    return s.timelines.items.find(item => item.id === id);
  },
  timelineCounts: s => s.timelineCounts,
  requestData: s => s.requestData,
  archived: s => s.requestData.archived,
  deleted: s => s.requestData.deleted,
  important: s => s.requestData.important,
  waitingOn: s => s.requestData.waitingOn,
  overdue: s => s.requestData.overdue,
  folder: s => s.requestData.folder,
  getContactById: (state) => (id) => {
    return state.contactsFilter.find(contact => contact.id === id);
  },
  getContactByIds: (state) => (ids) => {
    return state.contactsFilter.filter(contact => ids.includes(contact.id));
  }
};

export const actions = {
  updateRequestData({ commit, dispatch }, requestData) {
    dispatch('fetchTimelines', requestData);
  },
  addTaskIntoTimeline({ commit }, task) {
    commit("ADD_TASK", task);
  },
  collapse({ commit }, request) {
    commit("SET_ISCOLLAPSE", request);
  },
  expand({ commit }, request) {
    commit("SET_EXPAND", request);
  },
  collapseAll({ commit }) {
    commit("SET_ISCOLLAPSE_TRUE_All");
  },
  fetchCount({ dispatch }) {
    dispatch('fetchTimelineCount');
  },
  archiveTimelinesFromSidePanel({ commit }, request) {
    commit("ARCHIVE_TIMELINES_FROM_SIDE_PANEL", request);
  },
  archiveTimelinesFromSidePanelAll({ commit }, request) {
    commit("ARCHIVE_TIMELINES_FROM_SIDE_PANEL_ALL", request);
  },
  archiveTimelinesFromContactFilter({ commit }, request) {
    commit("ARCHIVE_TIMELINES_FROM_CONTACT_FILTER", request);
  },
  deleteTimelinesFromSidePanel({ commit }, request) {
    commit("DELETE_TIMELINES_FROM_SIDE_PANEL", request);
  },
  deleteTimelinesByContactIdFromSidePanel({ commit }, request) {
    commit("DELETE_TIMELINES_BY_CONTACTID_FROM_SIDE_PANEL", request);
  },
  deleteTimelinesByContactIdsFromSidePanel({ commit }, request) {
    commit("DELETE_TIMELINES_BY_CONTACTIDS_FROM_SIDE_PANEL", request);
  },
  deleteAllTimelinesFromSidePanel({ commit }, request) {
    commit("DELETE_TIMELINES_ALL", request);
  },
  removeFromContactsFilter({ commit }, id) {
    commit("REMOVE_FROM_CONTACT_FILER", id);
  },
  markAsImportantFromSidePanel({ commit }, request) {
    commit("MARK_AS_IMPORTANT_FROM_SIDE_PANEL", request);
  },
  markAllAsImportantFromSidePanel({ commit }, request) {
    commit("ALL_IMPORTANT", request);
  },
  markAllAsNotImportantFromSidePanel({ commit }, request) {
    commit("ALL_NOT_IMPORTANT", request);
  },
  markAsNotImportantFromSidePanel({ commit }, request) {
    commit("MARK_AS_NOT_IMPORTANT_FROM_SIDE_PANEL", request);
  },
  markAsReadFromSidePanel({ commit }, request) {
    commit("MARK_AS_READ_FROM_SIDE_PANEL", request);
  },
  markAllAsReadFromSidePanel({ commit }, request) {
    commit("MARK_ALL_AS_READ_FROM_SIDE_PANEL", request);
  },
  markAllAsUnReadFromSidePanel({ commit }, request) {
    commit("MARK_ALL_AS_UNREAD_FROM_SIDE_PANEL", request);
  },
  markAsUnreadFromSidePanel({ commit }, request) {
    commit("MARK_AS_UNREAD_FROM_SIDE_PANEL", request);
  },
  toggleWaitingOnTimelinesFromSidePanel({ commit }, request) {
    commit("TOGGLE_WAITINGON_TIMELINES_FROM_SIDE_PANEL", request);
  },
  fetchContact({ dispatch }) {
    dispatch('fetchContactsFilter');
  },
  addNoteIntoTimeline({ commit }, note) {
    commit("ADD_NOTE", note);
  },
  addEmailIntoTimeline({ commit }, email) {
    commit("ADD_EMAIL", email);
  },
  refreshSends({ dispatch },) {
    dispatch('refreshSend');
  },
  // A LOOK at the listing that changes nothing (busy-my-web#555): the SmartBox has to find the
  // thread the mail it just sent landed in, and every other listing call here commits what it reads
  // into the page the user is looking at. This one only answers the rows — the box matches them
  // itself (lib/sentThreadLookup.js) — so a send made from Home cannot repaint an open mailbox with
  // the Sent folder.
  async searchTimelineListing(_, params) {
    const response = await api.get('timeline-search/listing', { page: 1, pageSize: 25, order: 1, ...params });
    return response?.data?.items || [];
  },
  editNoteTimeline({ commit }, note) {
    commit("EDIT_NOTE", note);
  },
  editTaskTimeline({ commit }, task) {
    commit("EDIT_TASK", task);
  },
  editEventTimeline({ commit }, event) {
    commit("EDIT_EVENT", event);
  },
  addEventIntoTimeline({ commit }, event) {
    commit("ADD_EVENT", event);
  },
  deleteTimelineItems({ commit }, request) {
    commit("DELETE_TIMELINE_ITEMS", request);
  },

  // Load a single timeline. The WHOLE app fetches a thread through here, and it now uses the SAME
  // streaming path as fetchTimelineStream (SSE, GET v1/timeline/{id}/stream) — one canonical loader,
  // no plain-GET variant. It resolves with the canonical STORE timeline object as soon as the first
  // payload lands; the body keeps streaming into that same object, so callers that read it (props /
  // getters) update reactively as it fills in.
  fetchTimeline({ dispatch }, timelineId) {
    return dispatch('fetchTimelineStream', timelineId)
  },

  fetchTimelineStream({ commit, dispatch, rootGetters, getters }, timelineId) {
    // Deduplicate for the ENTIRE life of the stream. The promise resolves early (on the shell) so
    // the UI unblocks, but the dedup marker is held until the stream fully ends — so a second caller
    // that arrives after the shell (e.g. the email detail mounting) reuses THIS stream instead of
    // opening a second one. A second stream would re-commit the timeline shell and orphan the body
    // that was already streamed into the object a snapshot caller is holding.
    if (_pendingTimelineFetches.has(timelineId)) {
      return _pendingTimelineFetches.get(timelineId)
    }

    const user = rootGetters['user/user']
    const apiBase = `${process.env.VUE_APP_API_ADDRESS}/v1`
    const url = `${apiBase}/timeline/${timelineId}/stream`

    let resolvePromise
    const promise = new Promise((resolve) => { resolvePromise = resolve })
    _pendingTimelineFetches.set(timelineId, promise)

    const controller = new AbortController()
    // Only abort the socket here; the dedup marker is cleared once, in `finally`.
    const abort = () => { controller.abort() }

      ; (async () => {
        let resolved = false
        try {
          const response = await fetch(url, {
            credentials: 'include',
            headers: { 'X-CSRF': '1' },
            signal: controller.signal,
          })
          if (!response.ok || !response.body) { abort(); resolvePromise(getters.getTimelineById(timelineId) || null); return }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })

            let boundary
            while ((boundary = buffer.indexOf('\n\n')) !== -1) {
              const chunk = buffer.slice(0, boundary)
              buffer = buffer.slice(boundary + 2)

              let eventType = 'message'
              let data = ''
              for (const line of chunk.split('\n')) {
                if (line.startsWith('event:')) eventType = line.slice(6).trim()
                else if (line.startsWith('data:')) data = line.slice(5).trim()
              }

              try {
                if (eventType === 'timeline') {
                  const timeline = JSON.parse(data)
                  // SET_TIMELINE mutates this very object (it forces `seen = true`), so capture the
                  // arrival state FIRST — otherwise the mark-as-read check below can never be true.
                  const wasSeen = timeline.seen
                  commit('SET_TIMELINE', { timeline, email: user.email })
                  if (!wasSeen) {
                    commit('sidepanel/MARK_AS_READ', { ids: [timelineId] }, { root: true })
                  }
                  dispatch('sidepanel/updateTimeline', timeline, { root: true })
                  // Resolve immediately so the UI unblocks — bodies/extras keep streaming into the
                  // SAME store object (mutated in place), so snapshot callers stay live. Hand back
                  // the canonical store timeline, not the raw parse. The dedup marker stays set until
                  // the stream ends, so no second stream can replace this object.
                  if (!resolved) { resolved = true; resolvePromise(getters.getTimelineById(timelineId) || timeline) }
                } else if (eventType === 'email-body') {
                  const { id, contentHtml } = JSON.parse(data)
                  commit('PATCH_EMAIL_BODY', { timelineId, emailId: id, contentHtml })
                  dispatch('sidepanel/patchEmailBody', { timelineId, emailId: id, contentHtml }, { root: true })
                  dispatch('timelineSearch/patchEmailBody', { timelineId, emailId: id, contentHtml }, { root: true })
                  dispatch('draftEmail/patchDraftBody', { emailId: id, contentHtml }, { root: true })
                } else if (eventType === 'extras') {
                  const extras = JSON.parse(data)
                  commit('PATCH_TIMELINE_EXTRAS', { timelineId, extras })
                  dispatch('sidepanel/patchTimelineExtras', { timelineId, extras }, { root: true })
                } else if (eventType === 'done' || eventType === 'error') {
                  if (!resolved) { resolved = true; abort(); resolvePromise(getters.getTimelineById(timelineId) || null) }
                  return
                }
              } catch { /* ignore parse errors */ }
            }
          }
        } catch (e) {
          if (e?.name !== 'AbortError') { /* ignore */ }
        } finally {
          // The stream is fully over — clear the dedup marker exactly once here.
          _pendingTimelineFetches.delete(timelineId)
          if (!resolved) { resolved = true; resolvePromise(getters.getTimelineById(timelineId) || null) }
        }
      })()

    return promise
  },
  async fetchContactsFilter({ commit, rootGetters }) {
    const user = rootGetters['user/user'];

    // Load cached data immediately
    const cachedData = storageHelper.loadContactsFilter();
    if (cachedData && cachedData.length > 0) {
      commit("SET_CONTACTS_FILTER", cachedData);
    }

    // Fetch promise (do not wrap inside race!)
    const fetchPromise = api.get(`timeline-search/contactFilter`, { userEmail: user.email });

    // Timeout for initial UI response
    const timeoutPromise = new Promise(resolve =>
      setTimeout(() => resolve({ timeout: true }), 5000)
    );

    // Race only for FIRST RESPONSE
    const firstResult = await Promise.race([fetchPromise, timeoutPromise]);

    // If timeout happened → use cache and THEN let fetchPromise continue normally
    if (firstResult.timeout) {
      console.warn("Timeout: showing cached data, but still fetching in background");

      // Background refresh
      fetchPromise
        .then(response => {
          commit("SET_CONTACTS_FILTER", response.data);
        })
        .catch(err => {
        });

      return cachedData !== null;
    }

    // If API responded before timeout → normal path
    try {
      const response = firstResult;
      commit("SET_CONTACTS_FILTER", response.data);
      return true;
    } catch (err) {
    }
  },
  // One email at a time — that is what the caller (TimelineDetail.loadEmailDetails) sends, and the
  // BFF's GetEmailBodyRequest wants a LIST of ids under `emailIds`, so the single id is wrapped
  // here rather than making every caller know the wire shape.
  async fetchEmailBody({ commit }, { timelineId, emailId }) {
    return await api.getEmailBody(`email/body`, { emailIds: [emailId] }).then(response => {
      if (response) {
        // The BFF returns a LIST of bodies; take the first. PATCH_EMAIL_BODY is the same mutation
        // the SSE "email-body" event uses, so the fetched body lands exactly where consumers read
        // it (timeline.emailMessages[].contentHtml).
        const [body] = response.data ?? [];
        if (body?.contentHtml) {
          commit("PATCH_EMAIL_BODY", { timelineId: timelineId, emailId: emailId, contentHtml: body.contentHtml });
        }
      }
      return true;
    })
  },

  async archiveTimeline({ commit, dispatch, rootGetters, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForArchive(state, request);
    const count = calculatedRequest.ids.length;
    let message = noticeText('archive', count);
    if (count == 0) {
      message = alreadyText('archive', request.length);
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/archive`, { ids: calculatedRequest.ids });
        if (response) {
          const ids = [...calculatedRequest.ids];
          setTimeout(() => {
            toast.update(toastId, {
              ...undoableNotice('archive', message, () => dispatch('undoEmailAction', { kind: 'archive', ids })),
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          const user = rootGetters['user/user'];
          commit("MARK_AS_ARCHIVE", { request: calculatedRequest, user: user });
          setTimeout(() => dispatch('fetchContactsFilter'), 5000);
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      }
      catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to change emails folder",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
    }
  },
  // Undo on an action notice (busy-my-web#303): the inverse ENDPOINT on the very ids the action
  // touched, then a re-read. Not the inverse store action — its calculate* helper only sees rows
  // still on the page, and the rows an archive just removed are exactly the ones to bring back.
  async undoEmailAction({ dispatch }, { kind, ids } = {}) {
    const inverse = undoKindOf(kind);
    const endpoint = endpointFor(inverse);
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!inverse || !endpoint || !list.length) return false;
    try {
      const response = await api.insert(endpoint, { ids: list });
      if (!response) return false;
      toast.success(undoneText(kind), { theme: 'email' });
      // In and out of the bin changes which ongoings still have a member.
      if (kind === 'trash' || kind === 'restore') {
        dispatch('ongoing/syncAfterEntityChange', null, { root: true });
      }
      await dispatch('refreshTimelines');
      dispatch('fetchTimelineCount');
      return true;
    } catch (error) {
      toast.error(failedText(inverse), { theme: 'email' });
      return false;
    }
  },
  async unArchiveTimeline({ commit, dispatch, rootGetters, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForUnArchive(state, request);
    const count = calculatedRequest.ids.length;
    let message = noticeText('unarchive', count);
    if (count == 0) {
      message = alreadyText('unarchive', request.length);
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/unArchive`, { ids: calculatedRequest.ids });
        if (response) {
          const ids = [...calculatedRequest.ids];
          setTimeout(() => {
            toast.update(toastId, {
              ...undoableNotice('unarchive', message, () => dispatch('undoEmailAction', { kind: 'unarchive', ids })),
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          const user = rootGetters['user/user'];
          commit("MARK_AS_UNARCHIVE", { request: calculatedRequest, user: user });
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      }
      catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to change emails folder",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
    }
  },
  async deleteTimeline({ commit, dispatch, rootGetters, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForDelete(state, request);
    const count = calculatedRequest.ids.length;
    let message = noticeText('trash', count);
    if (count == 0) {
      message = alreadyText('trash', request.length);
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/moveToBin`, { ids: calculatedRequest.ids });
        if (response) {
          const ids = [...calculatedRequest.ids];
          setTimeout(() => {
            toast.update(toastId, {
              ...undoableNotice('trash', message, () => dispatch('undoEmailAction', { kind: 'trash', ids })),
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          const user = rootGetters['user/user'];
          commit("MOVE_TO_BIN", { request: calculatedRequest, user: user });
          // Binning the last email of an ongoing empties (hides) it server-side — re-read the ongoing
          // list so the sidebar drops it without a manual refresh. Moving it back brings it back.
          dispatch('ongoing/syncAfterEntityChange', null, { root: true });
          setTimeout(() => dispatch('fetchContactsFilter'), 5000);
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      } catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to delete emails",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
    }
  },
  async eraseTimeline({ commit, dispatch }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const count = request.ids.length
    let message = count === 1 ? 'Email deleted forever' : `${count} emails deleted forever`;
    try {
      const response = await api.deleteWithRequest(`timeline/erase`, request);
      if (response) {
        setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        commit("ERASE_EMAILS", request.ids);
        setTimeout(() => dispatch('fetchTimelineCount'), 5000);
        return true;
      }
    } catch (error) {
      setTimeout(() => {
        toast.update(toastId, {
          render: "Failed to erase emails",
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      return true;
    }
  },
  async moveToInbox({ commit, rootGetters, dispatch, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForMoveToInbox(state, request);

    const count = calculatedRequest.ids.length
    let message = noticeText('restore', count);
    try {
      const response = await api.insert(`timeline/moveToInbox`, { ids: calculatedRequest.ids });
      if (response) {
        const ids = [...calculatedRequest.ids];
        setTimeout(() => {
          toast.update(toastId, {
            ...undoableNotice('restore', message, () => dispatch('undoEmailAction', { kind: 'restore', ids })),
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        const user = rootGetters['user/user'];
        commit("MOVE_TO_INBOX", { request: calculatedRequest, user: user });
        // Out of the bin again — the ongoing this thread was the last member of comes back.
        dispatch('ongoing/syncAfterEntityChange', null, { root: true });
        setTimeout(() => dispatch('fetchTimelineCount'), 5000);
        setTimeout(() => dispatch('fetchContactsFilter'), 5000);
        return true;
      }
    } catch (error) {
      setTimeout(() => {
        toast.update(toastId, {
          render: "Failed to change emails folder",
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      return true;
    }
  },
  async markAsImportant({ commit, dispatch, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForMarkAsImportant(state, request);
    const count = calculatedRequest.ids.length;
    let message = count === 1 ? 'Email marked important' : `${count} emails marked important`;
    if (count == 0) {
      message = request.length === 1 ? 'Email already important' : `${request.length} emails already important`;
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/markAsImportant`, { ids: calculatedRequest.ids });
        if (response) {
          setTimeout(() => {
            toast.update(toastId, {
              render: message,
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          commit("MARK_AS_IMPORTANT", calculatedRequest);
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      } catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to change emails flags",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
    }
  },
  async markAsNotImportant({ commit, dispatch, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForMarkAsNotImportant(state, request);
    const count = calculatedRequest.ids.length;
    let message = count === 1 ? 'Email marked not important' : `${count} emails marked not important`;
    if (count == 0) {
      message = request.length === 1 ? 'Email already not important' : `${request.length} emails already not important`;
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/markAsNotImportant`, { ids: calculatedRequest.ids });
        if (response) {
          setTimeout(() => {
            toast.update(toastId, {
              render: message,
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          commit("MARK_AS_NOT_IMPORTANT", calculatedRequest);
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      } catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to change emails flags",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
    }
  },

  async toggleRead({ commit, dispatch }, timelineId) {
    return api.toggle(`timeline/${timelineId}/toggleRead`)
      .then((response) => {
        if (response) {
          commit("TOGGLE_READ", timelineId);
          toast.open({
            message: 'Email updated status',
            type: 'info',
            duration: 3000,
            dismissible: true
          })
          dispatch('fetchTimelineCount');
          return true;
        }
        return false;
      });
  },
  async markAsRead({ commit, dispatch, rootGetters, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForMarkAsRead(state, request);
    const count = calculatedRequest.ids.length;
    let message = noticeText('read', count);
    if (count == 0) {
      message = alreadyText('read', request.length);

      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/markAsRead`, { ids: calculatedRequest.ids });
        if (response) {
          const ids = [...calculatedRequest.ids];
          setTimeout(() => {
            toast.update(toastId, {
              ...undoableNotice('read', message, () => dispatch('undoEmailAction', { kind: 'read', ids })),
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          const user = rootGetters['user/user'];
          commit("MARK_AS_READ", { request: calculatedRequest, user: user });
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      } catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to change emails flags",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
    }
  },
  async markAsUnRead({ commit, dispatch, rootGetters, state }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateForMarkAsUnread(state, request);
    const count = calculatedRequest.ids.length;
    let message = noticeText('unread', count);
    if (count == 0) {
      message = alreadyText('unread', request.length);
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      try {
        const response = await api.insert(`timeline/markAsUnRead`, { ids: calculatedRequest.ids });
        if (response) {
          const ids = [...calculatedRequest.ids];
          setTimeout(() => {
            toast.update(toastId, {
              ...undoableNotice('unread', message, () => dispatch('undoEmailAction', { kind: 'unread', ids })),
              closeOnClick: true,
              closeButton: true,
              theme: 'email',
              isLoading: false,
            });
            toast.done(toastId);
          });
          const user = rootGetters['user/user'];
          commit("MARK_AS_UNREAD", { request: calculatedRequest, user: user });
          setTimeout(() => dispatch('fetchTimelineCount'), 5000);
          return true;
        }
      } catch (error) {
        setTimeout(() => {
          toast.update(toastId, {
            render: "Failed to change emails flags",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return false;
      }
    }
  },
  async toggleWaitingOn({ commit, dispatch }, request) {
    const toastId = toast.loading(
      'Please wait...', {
      theme: 'email',
    });
    const calculatedRequest = calculateToggleWaitingOn(state, request);
    let message = request.dueDate ? 'Email sets waiting-on' : "Email sets no waiting";
    try {
      const response = await api.insert(`timeline/toggleWaitingOn`, request)
      if (response) {
        setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'email',
            isLoading: false,
          });
          toast.done(toastId);
        });
        commit("UPDATE_WAITING", { request: request, calculatedRequest: calculatedRequest });
        setTimeout(() => dispatch('fetchTimelineCount'), 5000);
        true
      }
    } catch (error) {
      setTimeout(() => {
        toast.update(toastId, {
          render: "Failed to change emails flags",
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'email',
          isLoading: false,
        });
        toast.done(toastId);
      });
      return true;
    }
  },
  async refreshTimelines({ commit, state, dispatch }) {
    try {
      const response = await api.get('timeline/refresh', {});
      const { hasNewEmail, hasChanges } = response?.data || {};
      // hasChanges = Gmail changed mail already listed (Important, read, archive, deletes) — the
      // page must show it without a reload (busy-my-web#703).
      if (!hasNewEmail && !hasChanges) return;

      // Build the same request params as fetchTimelinesWithRetry
      const requestParams = { ...state.requestData };
      if (requestParams.archived === false) delete requestParams.archived;
      if (requestParams.deleted === false) delete requestParams.deleted;
      if (requestParams.important === false) delete requestParams.important;
      if (requestParams.waitingOn === false) delete requestParams.waitingOn;
      if (requestParams.overdue === false) delete requestParams.overdue;
      if (requestParams.folder === null) delete requestParams.folder;
      const viewAsked = JSON.stringify(state.requestData);

      // New mail reaches the search index through the timeline feeder chain; a Gmail-side change
      // is indexed before Refresh answers (busy-timeline-srv RebaseEmailItems), leaving only the
      // index's 1 s refresh interval.
      await new Promise(resolve => setTimeout(resolve, hasNewEmail ? 15000 : 1500));
      const listingResponse = await api.get('timeline-search/listing', requestParams);
      // The user moved to another folder/page meanwhile — that view fetched its own list.
      if (!listingResponse || JSON.stringify(state.requestData) !== viewAsked) return;

      if (hasChanges) {
        // The page as the server now has it: rows changed in place, rows that left the view gone.
        commit('RECONCILE_TIMELINES_PAGE', listingResponse.data);
      } else {
        // Only prepend genuinely new items — never replace the existing list
        commit('MERGE_NEW_TIMELINES', listingResponse.data);
      }
      dispatch('fetchTimelineCount', true);
      dispatch('fetchContactsFilter');
    } catch (error) {
      console.error('Error refreshing timelines:', error);
    }
  },
  async refreshSend({ commit, state, dispatch }) {
    const buildParams = () => {
      const requestParams = { ...state.requestData };
      if (requestParams.archived === false) delete requestParams.archived;
      if (requestParams.deleted === false) delete requestParams.deleted;
      if (requestParams.important === false) delete requestParams.important;
      if (requestParams.waitingOn === false) delete requestParams.waitingOn;
      if (requestParams.overdue === false) delete requestParams.overdue;
      requestParams.folder = 4
      return requestParams;
    };

    const isOnSent = () => router.currentRoute.value.path === '/emails/sent';

    const hasPendingMessageId = (data) => {
      if (!data?.items) return false;
      return data.items.some(timeline =>
        Array.isArray(timeline.emailMessages) &&
        timeline.emailMessages.some(m => m?.messageId == null)
      );
    };

    try {
      await new Promise(resolve => setTimeout(resolve, 3000));

      if (!isOnSent()) {
        dispatch('fetchTimelineCount', true);
        return;
      }

      const firstResponse = await api.get('timeline-search/listing', buildParams());
      if (firstResponse) {
        commit('MERGE_NEW_TIMELINES', firstResponse.data);
        dispatch('fetchTimelineCount', true);
        dispatch('fetchContactsFilter');
      }

      const MAX_ATTEMPTS = 10;
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        await new Promise(resolve => setTimeout(resolve, 10000));
        if (!isOnSent()) {
          dispatch('fetchTimelineCount', true);
          return;
        }

        const response = await api.get('timeline-search/listing', buildParams());
        if (!response) continue;

        commit('MERGE_NEW_TIMELINES', response.data);
        commit('UPDATE_EXISTING_TIMELINES', response.data);
        dispatch('fetchTimelineCount', true);

        if (!hasPendingMessageId(response.data)) break;
      }
    } catch (error) {
      console.error('Error refreshing timelines:', error);
    }
  },
  startAutoRefresh({ state, dispatch }) {
    if (state.refreshIntervalId) return;
    const intervalId = setInterval(() => {
      dispatch('refreshTimelines');
    }, 2 * 60 * 1000);
    state.refreshIntervalId = intervalId;
  },
  stopAutoRefresh({ state }) {
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      state.refreshIntervalId = null;
    }
  },
  async fetchTimelines({ commit, dispatch, state }, newRequestData) {
    let requestData = null;
    if (newRequestData === null) {
      requestData = {
        page: 1,
        pageSize: parseInt(localStorage.getItem('pageSize_inbox') || '50', 10),
        order: 1,
        search: '',
        archived: state.requestData.archived,
        deleted: state.requestData.deleted,
        important: state.requestData.important,
        waitingOn: state.requestData.waitingOn,
        overdue: state.requestData.overdue,
        folder: state.requestData.folder,
      }
    } else {
      requestData = newRequestData;
    } commit('SET_REQUEST_DATA', requestData);
    commit('SET_LOADING', true);

    try {
      await fetchTimelinesWithRetry(commit, requestData, 0);
      dispatch('globalCreationForms/mergeDrafts', null, { root: true });
    } finally {
      commit('SET_LOADING', false);
    }

  },
  async fetchTimelineCount({ commit, state, dispatch }, skipListRefresh = false) {
    // Load cached data immediately if available
    const cachedData = storageHelper.loadTimelineCounts();
    if (cachedData) {
      commit("SET_COUNT", cachedData);
    }

    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 5000; // 5 seconds

    while (retryCount <= maxRetries) {
      // Set up timeout promise (5 seconds)
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ isTimeout: true }), 5000);
      });

      // Set up fetch promise
      const fetchPromise = api.get(`timeline-search/count`, {})
        .then(response => ({ isTimeout: false, data: response }))
        .catch(error => ({ isTimeout: false, error }));

      try {
        const result = await Promise.race([fetchPromise, timeoutPromise]);

        if (result.isTimeout) {
          console.warn(`Fetch timeline count timed out after 5s (attempt ${retryCount + 1}/${maxRetries + 1})`);
          if (retryCount < maxRetries) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
          return cachedData !== null;
        }

        if (result.error) {
          console.error('Error fetching timeline count:', result.error);
          if (retryCount < maxRetries) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
          return cachedData !== null;
        }

        if (result.data && result.data.data) {
          const countData = result.data.data;

          // If totalCount is 0, retry up to 5 times
          if (countData.totalCount === 0 && retryCount < maxRetries) {
            retryCount++;
            console.log(`Timeline count is 0, retrying... (attempt ${retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }

          // Either have data or max retries reached
          commit("SET_COUNT", countData);
          storageHelper.saveTimelineCounts(countData);

          // If we got valid data, fetch timelines list (skip on background refresh to avoid replacing the list)
          if (countData.totalCount > 0 && !skipListRefresh) {
            dispatch('fetchTimelines', null);
            dispatch('fetchContactsFilter', null);
          }

          return true;
        }

        return false;
      } catch (error) {
        console.error('Error fetching timeline count:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        return cachedData !== null;
      }
    }
  },
  scrollToTimeline({ state }, id) {
    const element = state.timelineRefs[id];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`Element with ID timelineItem-${id} not found.`);
    }
  }
};
async function fetchTimelinesWithRetry(commit, requestData, retryCount) {
  try {
    let response = null;
    let requestParams = { ...requestData };
    if (requestParams.archived === false) {
      delete requestParams.archived;
    }
    if (requestParams.deleted === false) {
      delete requestParams.deleted;
    }
    if (requestParams.important === false) {
      delete requestParams.important;
    }
    if (requestParams.waitingOn === false) {
      delete requestParams.waitingOn;
    }
    if (requestParams.overdue === false) {
      delete requestParams.overdue;
    }
    if (requestParams.folder === null) {
      delete requestParams.folder;
    }
    response = await api.get('timeline-search/listing', requestParams);
    if (response) {
      commit("SET_TIMELINES", response.data);
      commit('SET_LOADING', false);
      // let fetchEmailBodies = api.getEmailBody(`email/body`, { emailId: emailIds });
    }
  } catch (error) {
    console.error('Error fetching timelines:', error);
    if (retryCount < 3) {
      await new Promise(resolve => setTimeout(resolve, 10000));
      await fetchTimelinesWithRetry(commit, requestData, retryCount + 1);
    } else {
      console.error('Max retries reached. Failed to fetch timelines.');
    }
  }
}
function calculateForMarkAsUnread(state, request) {
  const timelinesInbox = state.timelines.items.filter(item => request.includes(item.id) && item.seen && !item.isArchive);
  const timelinesArchive = state.timelines.items.filter(item => request.includes(item.id) && item.seen && item.isArchive);
  let ids = [...timelinesInbox.map(i => i.id), ...timelinesArchive.map(i => i.id)];
  let inboxCount = 0;
  let archiveCount = 0;
  timelinesInbox.forEach(item => {
    if (item.emailMessages) {
      inboxCount += item.emailMessages.filter(ti => ti.seen).length;
    }
  });
  timelinesArchive.forEach(item => {
    if (item.emailMessages) {
      archiveCount += item.emailMessages.filter(ti => ti.seen).length;
    }
  });
  return { ids, inboxCount, archiveCount };
}
function calculateForMarkAsRead(state, request) {
  const timelinesInbox = state.timelines.items.filter(item => request.includes(item.id) && !item.seen && !item.isArchive);
  const timelinesArchive = state.timelines.items.filter(item => request.includes(item.id) && !item.seen && item.isArchive);
  let ids = [...timelinesInbox.map(i => i.id), ...timelinesArchive.map(i => i.id)];
  let inboxCount = 0;
  let archiveCount = 0;
  timelinesInbox.forEach(item => {
    if (item.emailMessages) {
      inboxCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  timelinesArchive.forEach(item => {
    if (item.emailMessages) {
      archiveCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  return { ids, inboxCount, archiveCount };
}
function calculateForMarkAsImportant(state, request) {
  const importantTimelines = state.timelines.items.filter(item => request.includes(item.id) && !item.isImportant);
  let ids = importantTimelines.map(i => i.id);
  return { ids };
}
function calculateForMarkAsNotImportant(state, request) {
  const importantTimelines = state.timelines.items.filter(item => request.includes(item.id) && item.isImportant);
  let ids = importantTimelines.map(i => i.id);
  return { ids };
}
function calculateForArchive(state, request) {
  const timelines = state.timelines.items.filter(item => request.includes(item.id) && !item.isArchive);
  let ids = timelines.map(i => i.id);
  let unSeenCount = 0;
  timelines.forEach(item => {
    if (item.emailMessages) {
      unSeenCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  let importantCount = timelines.filter(i => i.isImportant).length;
  let waitingOnCount = timelines.filter(i => i.waitingOn).length;
  let overdueCount = timelines.filter(i => i.dueDate && new Date(i.dueDate) <= new Date()).length;
  return { ids, unSeenCount, importantCount, waitingOnCount, overdueCount };
}
function calculateForUnArchive(state, request) {
  const timelines = state.timelines.items.filter(item => request.includes(item.id) && item.isArchive);
  let ids = timelines.map(i => i.id);
  let unSeenCount = 0;
  timelines.forEach(item => {
    if (item.emailMessages) {
      unSeenCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  let importantCount = timelines.filter(i => i.isImportant).length;
  let waitingOnCount = timelines.filter(i => i.waitingOn).length;
  let overdueCount = timelines.filter(i => i.dueDate && new Date(i.dueDate) <= new Date()).length;
  return { ids, unSeenCount, importantCount, waitingOnCount, overdueCount };
}
function calculateForMoveToInbox(state, request) {
  const timelines = state.timelines.items.filter(item => request.includes(item.id));
  let ids = timelines.map(i => i.id);
  let unSeenCount = 0;
  timelines.forEach(item => {
    if (item.emailMessages) {
      unSeenCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  let importantCount = timelines.filter(i => i.isImportant).length;
  let waitingOnCount = timelines.filter(i => i.waitingOn).length;
  let overdueCount = timelines.filter(i => i.dueDate && new Date(i.dueDate) <= new Date()).length;
  return { ids, unSeenCount, importantCount, waitingOnCount, overdueCount };
}
function calculateForDelete(state, request) {
  const timelinesInbox = state.timelines.items.filter(item => request.includes(item.id) && !item.isArchive);
  const timelinesArchive = state.timelines.items.filter(item => request.includes(item.id) && item.isArchive);
  const timelines = [...timelinesInbox, ...timelinesArchive]
  let ids = [...timelinesInbox.map(i => i.id), ...timelinesArchive.map(i => i.id)];
  let inboxCount = timelinesInbox.length;
  let inboxUnSeenCount = 0;
  let archiveCount = timelinesArchive.length;
  let archiveUnSeenCount = 0;
  timelinesInbox.forEach(item => {
    if (item.emailMessages) {
      inboxUnSeenCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  timelinesArchive.forEach(item => {
    if (item.emailMessages) {
      archiveUnSeenCount += item.emailMessages.filter(ti => !ti.seen).length;
    }
  });
  let importantCount = timelines.filter(i => i.isImportant).length;
  let waitingOnCount = timelines.filter(i => i.waitingOn).length;
  let overdueCount = timelines.filter(i => i.dueDate && new Date(i.dueDate) <= new Date()).length;
  return { ids, inboxCount, inboxUnSeenCount, archiveCount, archiveUnSeenCount, importantCount, waitingOnCount, overdueCount };
}
function calculateToggleWaitingOn(state, request) {
  const timeline = state.timelines.items.find(t => t.id === request.timelineId);
  if (!timeline) return { addWaitingOn: false, addOverDue: false, removeOverDue: false };
  let addWaitingOn = !timeline.waitingOn;
  let addOverDue = false;
  let removeOverDue = false;
  const currentDateTime = new Date();
  const dueDateTime = request.dueDate ? new Date(request.dueDate) : null;
  const wasOverDue = timeline.dueDate && new Date(timeline.dueDate) <= currentDateTime;

  if (dueDateTime) {
    if (addWaitingOn) {
      addOverDue = dueDateTime <= currentDateTime;
    } else {
      if (dueDateTime > currentDateTime) {
        removeOverDue = true;
      }
    }
  } else {
    removeOverDue = wasOverDue;
  }
  return { addWaitingOn, addOverDue, removeOverDue };
}
function ensureClientDeletedStore(timeline) {
  if (!timeline._clientDeletedItems) {
    timeline._clientDeletedItems = {
      notes: new Set(),
      tasks: new Set(),
      events: new Set()
    };
  } else {
    ['notes', 'tasks', 'events'].forEach(key => {
      const value = timeline._clientDeletedItems[key];
      if (Array.isArray(value)) {
        timeline._clientDeletedItems[key] = new Set(value);
      } else if (!(value instanceof Set)) {
        timeline._clientDeletedItems[key] = new Set();
      }
    });
  }
  return timeline._clientDeletedItems;
}
function filterTimelineWithClientDeletes(timeline, clientDeleted) {
  if (!clientDeleted) return timeline;
  const deletedNotes = clientDeleted.notes || new Set();
  const deletedTasks = clientDeleted.tasks || new Set();
  const deletedEvents = clientDeleted.events || new Set();

  if (Array.isArray(timeline.notes)) {
    timeline.notes = timeline.notes.filter(n => !deletedNotes.has(n.id));
  }
  if (Array.isArray(timeline.tasks)) {
    timeline.tasks = timeline.tasks.filter(t => !deletedTasks.has(t.id));
  }
  if (Array.isArray(timeline.events)) {
    timeline.events = timeline.events.filter(e => !deletedEvents.has(e.id));
  }
  if (Array.isArray(timeline.timelineItems)) {
    timeline.timelineItems = timeline.timelineItems.filter(item => {
      if (item.itemType === 4) return !deletedNotes.has(item.id);
      if (item.itemType === 2) return !deletedTasks.has(item.id);
      if (item.itemType === 3) return !deletedEvents.has(item.id);
      return true;
    });
  }

  if ('noteCount' in timeline) timeline.noteCount = Array.isArray(timeline.notes) ? timeline.notes.length : 0;
  if ('taskCount' in timeline) timeline.taskCount = Array.isArray(timeline.tasks) ? timeline.tasks.length : 0;
  if ('reminderCount' in timeline) timeline.reminderCount = Array.isArray(timeline.events) ? timeline.events.length : 0;

  return timeline;
}

function sortContactsFilterByInboxOrder(state) {
  const contactIds = new Set(state.contactsFilter.map(c => c.id));
  const orderedIds = [];
  const seen = new Set();
  for (const timeline of state.timelines.items) {
    if (timeline.isArchive || timeline.isDeleted) continue;
    for (const p of timeline.participants) {
      if (contactIds.has(p.id) && !seen.has(p.id)) {
        seen.add(p.id);
        orderedIds.push(p.id);
      }
    }
  }
  state.contactsFilter.sort((a, b) => {
    const posA = orderedIds.indexOf(a.id);
    const posB = orderedIds.indexOf(b.id);
    return (posA === -1 ? Infinity : posA) - (posB === -1 ? Infinity : posB);
  });
}

export const mutations = {
  RESET_STATE(state, archived, deleted, important, waitingOn, overdue, folder) {
    Object.assign(state, initialState(archived, deleted, important, waitingOn, overdue, folder));
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  SET_LOADING(s, isLoading) {
    s.isLoading = isLoading;
  },
  SET_IsAllEmailsSynced(s, isAllEmailsSynced) {
    s.isAllEmailsSynced = isAllEmailsSynced;
  },
  SET_CONTACTS_FILTER(s, contacts) {
    s.contactsFilter = contacts;
    s.isContactsFilterLoading = false;
    storageHelper.saveContactsFilter(contacts);
  },
  SET_COUNT(s, response) {
    s.timelineCounts = response;
    storageHelper.saveTimelineCounts(response);
  },
  REMOVE(s, ids) {
    s.timelines.items = s.timelines.items.filter(item => !ids.includes(item.id));
  },
  REMOVE_FROM_CONTACT_FILER(s, id) {
    s.contactsFilter = s.contactsFilter.filter(item => item.id !== id);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  ADD_TASK(s, task) {
    const timeline = s.timelines.items.find(t => t.id === task.timelineId);
    if (timeline) {
      timeline.timelineItems.push({
        id: task.id,
        assignDate: task.createDate,
        isDeleted: task.isDeleted,
        isImportant: task.isImportant,
        itemType: 2,
        timelineId: task.timelineId,
        title: task.title,
        newlyAdded: true
      })
      timeline.tasks.push(task);
      timeline.taskCount++;
    }
  },
  ADD_NOTE(s, note) {
    const timeline = s.timelines.items.find(t => t.id === note.timelineId);
    if (timeline) {
      timeline.timelineItems.push({
        id: note.id,
        assignDate: note.createDate,
        isDeleted: note.isDeleted,
        isImportant: note.isImportant,
        itemType: 4,
        timelineId: note.timelineId,
        title: note.title,
        newlyAdded: true

      })
      timeline.notes.push(note);
      timeline.noteCount++;
    }
  },
  ADD_EMAIL(s, email) {
    const timeline = s.timelines.items.find(t => t.id === email.timelineId);

    if (timeline) {
      const sharedId = crypto.randomUUID();
      const now = new Date();
      const timelineItem = {
        id: sharedId,
        timelineId: email.timelineId,
        assignDate: now,
        itemType: 1,
        title: email.subject,
        isDeleted: false,
        seen: true,
        mainFolder: 0,
        newlyAdded: true,
      };
      const rawEmail = toRaw(email);
      const emailMessage = {
        id: sharedId,
        timelineId: rawEmail.timelineId,
        subject: rawEmail.subject,
        contentHtml: rawEmail.body,
        messageId: rawEmail.messageId || '',
        isImportant: rawEmail.isImportant || false,
        mainFolder: 4,
        senders: rawEmail.fromAddress ? `${rawEmail.fromAddress.name} <${rawEmail.fromAddress.emailAddress}>` : '',
        recipientsTo: rawEmail.toAddresses ? rawEmail.toAddresses.map(a => `${a.name} <${a.emailAddress}>`).join(';') : '',
        recipientsCc: rawEmail.ccAddresses ? rawEmail.ccAddresses.map(a => `${a.name} <${a.emailAddress}>`).join(';') : '',
        recipientsBcc: rawEmail.bccAddresses ? rawEmail.bccAddresses.map(a => `${a.name} <${a.emailAddress}>`).join(';') : '',
        attachments: rawEmail.attachments ? [...rawEmail.attachments] : [],
        assignDate: now.toISOString(),
        isCollapsed: true
      };
      timeline.timelineItems.push(timelineItem);
      if (!timeline.emailMessages) timeline.emailMessages = [];
      timeline.emailMessages.push(emailMessage);
      timeline.emailCount = (timeline.emailCount || 0) + 1;
    }
  },
  EDIT_NOTE(s, note) {
    const timeline = s.timelines.items.find(t => t.id === note.timelineId);
    if (timeline) {
      // Update timelineItems
      const itemIdx = timeline.timelineItems.findIndex(item => item.id === note.id && item.itemType === 4);
      if (itemIdx !== -1) {
        const existingItem = timeline.timelineItems[itemIdx];
        timeline.timelineItems[itemIdx] = {
          id: note.id,
          isImportant: note.isImportant,
          itemType: 4,
          timelineId: note.timelineId,
          title: note.title,
          content: note.content,
          newlyAdded: existingItem.newlyAdded || false
        };
      }
      // Update note in notes array
      const noteIdx = timeline.notes.findIndex(n => n.id === note.id);
      if (noteIdx !== -1) {
        timeline.notes[noteIdx] = note;
      }
      // No count increment, just edit
    }
  },
  EDIT_TASK(s, task) {
    const timeline = s.timelines.items.find(t => t.id === task.timelineId);
    if (timeline) {
      // Update timelineItems
      const itemIdx = timeline.timelineItems.findIndex(item => item.id === task.id && item.itemType === 2);
      if (itemIdx !== -1) {
        const existingItem = timeline.timelineItems[itemIdx];
        // Map string status to TaskStatus enum value
        let statusValue = 1; // None = 1 << 0
        if (task.status === 'done' || task.status === 2 || task.status === (1 << 1)) {
          statusValue = 2; // Done = 1 << 1
        } else if (task.status === 'waitingon' || task.status === 4 || task.status === (1 << 2)) {
          statusValue = 4; // WaitingOn = 1 << 2
        } else if (task.status === 'someday' || task.status === 8 || task.status === (1 << 3)) {
          statusValue = 8; // SomeDay = 1 << 3
        }
        const newItem = {
          ...existingItem,
          id: task.id,
          isImportant: task.isImportant,
          itemType: 2,
          timelineId: task.timelineId,
          title: task.title,
          content: task.content,
          taskInfo: {
            dueDate: task.dueDate,
            isDone: task.completedDate !== null,
            status: statusValue
          },
          newlyAdded: existingItem.newlyAdded || false
        };
        // Create new array to ensure Vue detects the change
        timeline.timelineItems = timeline.timelineItems.map((item, idx) =>
          idx === itemIdx ? newItem : item
        );
      }
      // Update task in tasks array
      const taskIdx = timeline.tasks.findIndex(n => n.id === task.id);
      if (taskIdx !== -1) {
        timeline.tasks[taskIdx] = task;
      }
      // No count increment, just edit
    }
  },
  DELETE_TIMELINE_ITEMS(s, request) {
    const timeline = s.timelines?.items?.find(t => t.id === request.timelineId);
    if (!timeline) return;

    const id = request.itemId;
    const clientDeleted = ensureClientDeletedStore(timeline);


    switch (request.itemType) {
      case 'notes':
        if (Array.isArray(timeline.notes)) {
          timeline.notes = timeline.notes.filter(n => n.id !== id); // reassignment is fine
          if ('noteCount' in timeline) timeline.noteCount = timeline.notes.length;
        }
        if (Array.isArray(timeline.timelineItems)) {
          timeline.timelineItems = timeline.timelineItems.filter(item => !(item.itemType === 4 && item.id === id));
        }
        clientDeleted.notes.add(id);
        break;

      case 'events':
        if (Array.isArray(timeline.events)) {
          const eventIdx = timeline.events.findIndex(e => e.id === id);
          if (eventIdx !== -1) timeline.events.splice(eventIdx, 1);
          if ('reminderCount' in timeline) timeline.reminderCount = timeline.events.length;
        }
        if (Array.isArray(timeline.timelineItems)) {
          timeline.timelineItems = timeline.timelineItems.filter(item => !(item.itemType === 3 && item.id === id));
        }
        clientDeleted.events.add(id);
        break;

      case 'tasks':
        if (Array.isArray(timeline.tasks)) {
          const taskIdx = timeline.tasks.findIndex(t => t.id === id);
          if (taskIdx !== -1) timeline.tasks.splice(taskIdx, 1);
          if ('taskCount' in timeline) timeline.taskCount = timeline.tasks.length;
        }
        if (Array.isArray(timeline.timelineItems)) {
          timeline.timelineItems = timeline.timelineItems.filter(item => !(item.itemType === 2 && item.id === id));
        }
        clientDeleted.tasks.add(id);
        break;

      case 'emails':
        // Find the email by messageId and get its UUID id
        let emailUuid = null;
        if (Array.isArray(timeline.emailMessages)) {
          const email = timeline.emailMessages.find(e => e.messageId === id);
          if (email) {
            emailUuid = email.id;
            timeline.emailMessages = timeline.emailMessages.filter(e => e.messageId !== id);
            if ('emailCount' in timeline) timeline.emailCount = timeline.emailMessages.length;
          }
        }
        // Remove the corresponding timeline item by UUID
        if (emailUuid && Array.isArray(timeline.timelineItems)) {
          timeline.timelineItems = timeline.timelineItems.filter(item => !(item.itemType === 1 && item.id === emailUuid));
        }
        clientDeleted.emails = clientDeleted.emails || new Set();
        clientDeleted.emails.add(id);
        break;

      default:
        break;
    }
  },

  ADD_EVENT(s, newEvent) {
    const event = newEvent.data[0]
    const timeline = s.timelines.items.find(t => t.id === event.timelineId);
    if (timeline) {
      timeline.timelineItems.push({
        id: event.id,
        assignDate: event.createDate,
        isDeleted: event.isDeleted,
        isImportant: event.isImportant,
        itemType: 3,
        timelineId: event.timelineId,
        title: event.summary,
        newlyAdded: true,
        start: event.start,
        end: event.end
      })
      // Normalize: ensure startDateTime/endDateTime exist for the edit form
      const normalizedEvent = {
        ...event,
        startDateTime: event.startDateTime || event.start,
        endDateTime: event.endDateTime || event.end,
      };
      timeline.events.push(normalizedEvent);
      timeline.eventCount++;
    }
  },
  EDIT_EVENT(s, newEvent) {
    const event = newEvent
    const timeline = s.timelines.items.find(t => t.id === event.timelineId);
    if (timeline) {
      const itemIdx = timeline.timelineItems.findIndex(item => item.id === event.id && item.itemType === 3);
      if (itemIdx !== -1) {
        const existingItem = timeline.timelineItems[itemIdx];
        timeline.timelineItems[itemIdx] = {
          id: event.id,
          assignDate: event.createDate,
          isDeleted: event.isDeleted,
          isImportant: event.isImportant,
          itemType: 3,
          timelineId: event.timelineId,
          title: event.summary,
          newlyAdded: existingItem.newlyAdded || false,
          start: event.start,
          end: event.end
        };
      }
      const eventIdx = timeline.events.findIndex(e => e.id === event.id);
      if (eventIdx !== -1) {
        // Normalize: ensure startDateTime/endDateTime exist for the edit form
        timeline.events[eventIdx] = {
          ...event,
          startDateTime: event.startDateTime || event.start,
          endDateTime: event.endDateTime || event.end,
        };
      }
    }
  },
  SET_ISCOLLAPSE(s, request) {
    const index = s.timelines.items.findIndex(item => item.id === request.id);
    if (index !== -1) {
      s.timelines.items[index].isCollapsed = !s.timelines.items[index].isCollapsed;
    };
  },
  SET_EXPAND(s, request) {
    const index = s.timelines.items.findIndex(item => item.id === request.id);
    if (index !== -1) {
      s.timelines.items[index].isCollapsed = false;
    };
  },
  PATCH_EMAIL_BODY(s, { timelineId, emailId, contentHtml }) {
    const timeline = s.timelines.items.find(t => t.id === timelineId)
    if (!timeline?.emailMessages) return
    const email = timeline.emailMessages.find(e => e.id === emailId)
    if (email) email.contentHtml = contentHtml
  },
  PATCH_TIMELINE_EXTRAS(s, { timelineId, extras }) {
    const timeline = s.timelines.items.find(t => t.id === timelineId)
    if (!timeline) return
    if (extras.notes?.length) timeline.notes = extras.notes
    if (extras.tasks?.length) {
      timeline.tasks = extras.tasks
      // Sync task completion status in timelineItems when tasks are updated
      if (timeline.timelineItems) {
        const taskMap = new Map(extras.tasks.map(t => [t.id, t]))
        timeline.timelineItems = timeline.timelineItems.map(item => {
          if (item.itemType === 2) {
            const task = taskMap.get(item.id)
            if (task) {
              return {
                ...item,
                taskInfo: {
                  ...(item.taskInfo || {}),
                  isDone: task.completedDate !== null
                }
              }
            }
          }
          return item
        })
      }
    }
    if (extras.events?.length) timeline.events = extras.events
  },
  SET_TIMELINE(s, { timeline, email }) {
    let timelineParticipants = timeline.participants.filter(p => p.email !== email)
    let contactInContactFilter = s.contactsFilter.filter(c => timelineParticipants.some(p => p.id === c.id))

    // Sync taskInfo with tasks array to ensure consistency
    if (timeline.timelineItems && timeline.tasks) {
      const taskMap = new Map(timeline.tasks.map(t => [t.id, t]))
      timeline.timelineItems = timeline.timelineItems.map(item => {
        if (item.itemType === 2) {
          const task = taskMap.get(item.id)
          if (task) {
            return {
              ...item,
              taskInfo: {
                ...(item.taskInfo || {}),
                isDone: task.completedDate !== null
              }
            }
          }
        }
        return item
      })
    }

    const index = s.timelines.items.findIndex(item => item.id === timeline.id);
    if (index !== -1) {
      const foundItem = s.timelines.items[index];
      const clientDeleted = ensureClientDeletedStore(foundItem);
      timeline.seen = true;
      timeline.isCollapsed = foundItem.isCollapsed;
      timeline.attachments = foundItem.attachments;
      timeline.attachmentsCount = foundItem.attachmentsCount;
      // Pass participant/date fields from listing data — API may return them as null or ""
      if (foundItem.emailMessages) {
        const participantFields = ['senders', 'recipientsTo', 'recipientsCc', 'recipientsBcc', 'receivedUtc', 'sentUtc']
        timeline.emailMessages = timeline.emailMessages.map(newEmail => {
          const existing = foundItem.emailMessages.find(e => e.id === newEmail.id)
          if (!existing) return newEmail
          const merged = { ...existing, ...newEmail }
          if (existing.seen) merged.seen = true
          participantFields.forEach(key => {
            if (!merged[key] && existing[key]) merged[key] = existing[key]
          })
          // A re-fetch's shell has no body yet — keep the one we already streamed in.
          if (!merged.contentHtml && existing.contentHtml) merged.contentHtml = existing.contentHtml
          return merged
        })
      }
      if (!timeline.isDeleted && !foundItem.seen) {
        // Only count emails that were unseen in the existing store — prevents double-decrement on re-expand
        const existingUnseenCount = foundItem.emailMessages
          ? foundItem.emailMessages.filter(e => !e.seen).length
          : 1;
        if (timeline.isArchive) {
          s.timelineCounts.archiveUnSeenCount = Math.max(0, s.timelineCounts.archiveUnSeenCount - existingUnseenCount);
        } else {
          s.timelineCounts.unSeenCount = Math.max(0, s.timelineCounts.unSeenCount - existingUnseenCount);
          contactInContactFilter.forEach(contact => {
            Object.assign(contact, { unSeenCount: Math.max(0, contact.unSeenCount - existingUnseenCount) });
          });
        }
      }
      timeline.emailMessages.forEach(e => {
        e.seen = true;
        e.isCollapsed = e.mainFolder !== 8;
      });
      timeline._clientDeletedItems = clientDeleted;
      filterTimelineWithClientDeletes(timeline, clientDeleted);
      s.timelines.items[index] = timeline;
    }
    else {
      const wasAlreadySeen = timeline.seen;
      timeline.seen = true;
      timeline.isCollapsed = false;
      if (!timeline.isDeleted && !wasAlreadySeen) {
        const unseenEmails = timeline.emailMessages.filter(e => !e.seen);
        if (timeline.isArchive) {
          s.timelineCounts.archiveUnSeenCount = Math.max(0, s.timelineCounts.archiveUnSeenCount - unseenEmails.length);
        } else {
          s.timelineCounts.unSeenCount = Math.max(0, s.timelineCounts.unSeenCount - unseenEmails.length);
          contactInContactFilter.forEach(contact => {
            Object.assign(contact, { unSeenCount: Math.max(0, contact.unSeenCount - unseenEmails.length) });
          });
        }
      }
      timeline.emailMessages.forEach(e => {
        e.seen = true;
        e.isCollapsed = e.mainFolder !== 8;
      });
      filterTimelineWithClientDeletes(timeline, timeline._clientDeletedItems);
      s.timelines.items.push(timeline);
    }
    s.isLoading = false;
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  SET_TIMELINES(s, data) {
    s.timelines = {};
    // Sync task completion status in all timelines
    if (data?.items) {
      data.items.forEach(timeline => {
        if (timeline.timelineItems && timeline.tasks) {
          const taskMap = new Map(timeline.tasks.map(t => [t.id, t]))
          timeline.timelineItems = timeline.timelineItems.map(item => {
            if (item.itemType === 2) {
              const task = taskMap.get(item.id)
              if (task) {
                return {
                  ...item,
                  taskInfo: {
                    ...(item.taskInfo || {}),
                    isDone: task.completedDate !== null
                  }
                }
              }
            }
            return item
          })
        }
        // Set isCollapsed based on draft status: drafts expand, others collapse
        if (timeline.emailMessages && Array.isArray(timeline.emailMessages)) {
          timeline.emailMessages = timeline.emailMessages.map(email => ({
            ...email,
            isCollapsed: email.mainFolder !== 8
          }))
        }
      })
    }
    s.timelines = data;
  },
  MERGE_NEW_TIMELINES(s, data) {
    if (!data || !data.items) return;
    const existingIds = new Set(s.timelines.items.map(item => item.id));
    const newItems = data.items.filter(item => !existingIds.has(item.id));
    if (newItems.length > 0) {
      // Sync taskInfo with tasks array for new items
      newItems.forEach(timeline => {
        if (timeline.timelineItems && timeline.tasks) {
          const taskMap = new Map(timeline.tasks.map(t => [t.id, t]))
          timeline.timelineItems = timeline.timelineItems.map(item => {
            if (item.itemType === 2) {
              const task = taskMap.get(item.id)
              if (task) {
                return {
                  ...item,
                  taskInfo: {
                    ...(item.taskInfo || {}),
                    isDone: task.completedDate !== null
                  }
                }
              }
            }
            return item
          })
        }
        // Set isCollapsed based on draft status: drafts expand, others collapse
        if (timeline.emailMessages && Array.isArray(timeline.emailMessages)) {
          timeline.emailMessages = timeline.emailMessages.map(email => ({
            ...email,
            isCollapsed: email.mainFolder !== 8
          }))
        }
      })
      s.timelines.items = [...newItems, ...s.timelines.items];
      s.timelines.totalCount = (s.timelines.totalCount || 0) + newItems.length;
    }
  },
  // A re-read of the page being shown (busy-my-web#703): the server's rows in the server's order,
  // so a thread changed in Gmail updates in place and one that left this view (archived, deleted,
  // no longer Important) drops out. A thread the user had opened keeps its expanded messages.
  RECONCILE_TIMELINES_PAGE(s, data) {
    if (!data || !Array.isArray(data.items)) return;
    const expanded = new Set();
    (s.timelines.items || []).forEach(timeline => {
      (timeline.emailMessages || []).forEach(email => {
        if (email && email.isCollapsed === false) expanded.add(email.id);
      });
    });
    const items = data.items.map(incoming => {
      const updated = { ...incoming };
      if (updated.timelineItems && updated.tasks) {
        const taskMap = new Map(updated.tasks.map(t => [t.id, t]));
        updated.timelineItems = updated.timelineItems.map(item => {
          const task = item.itemType === 2 ? taskMap.get(item.id) : null;
          return task
            ? { ...item, taskInfo: { ...(item.taskInfo || {}), isDone: task.completedDate !== null } }
            : item;
        });
      }
      if (Array.isArray(updated.emailMessages)) {
        updated.emailMessages = updated.emailMessages.map(email => ({
          ...email,
          isCollapsed: email.mainFolder !== 8 && !expanded.has(email.id)
        }));
      }
      return updated;
    });
    s.timelines = { ...s.timelines, ...data, items };
  },
  UPDATE_EXISTING_TIMELINES(s, data) {
    if (!data || !data.items) return;
    const incomingMap = new Map(data.items.map(item => [item.id, item]));
    s.timelines.items = s.timelines.items.map(existing => {
      const incoming = incomingMap.get(existing.id);
      if (!incoming) return existing;
      const updated = { ...incoming };
      if (updated.timelineItems && updated.tasks) {
        const taskMap = new Map(updated.tasks.map(t => [t.id, t]));
        updated.timelineItems = updated.timelineItems.map(item => {
          if (item.itemType === 2) {
            const task = taskMap.get(item.id);
            if (task) {
              return {
                ...item,
                taskInfo: {
                  ...(item.taskInfo || {}),
                  isDone: task.completedDate !== null
                }
              };
            }
          }
          return item;
        });
      }
      if (updated.emailMessages && Array.isArray(updated.emailMessages)) {
        updated.emailMessages = updated.emailMessages.map(email => ({
          ...email,
          isCollapsed: email.mainFolder !== 8
        }));
      }
      return updated;
    });
  },
  SET_REQUEST_DATA(s, data) {
    s.requestData.page = data.page
    s.requestData.pageSize = data.pageSize
    s.requestData.order = data.order
    s.requestData.search = data.search
    s.requestData.archived = data.archived
    s.requestData.deleted = data.deleted
    s.requestData.important = data.important
    s.requestData.waitingOn = data.waitingOn
    s.requestData.overdue = data.overdue
    s.requestData.folder = data.folder
  },
  UPDATE_IMPORTANT(s, data) {
    data.response.forEach(response => {
      const timeline = s.timelines.items.find(t => t.id === response.id);
      if (timeline) {
        timeline.isImportant = data.important;
      }
    })
  },
  SET_ERROR_FOR_CONTACT(s, err) {
    s.contactsFilterLoadingError = err;
    s.isContactsFilterLoading = false;
  },
  SET_ERROR(s, err) {
  },
  MARK_AS_READ(s, { request, user }) {
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline && !s.requestData.deleted) {
        let timelineParticipants = timeline.participants.filter(p => p.email !== user.email)
        let timelineEmailsCount = timeline.timelineItems.filter(ti => ti.itemType === 1).length
        let contactInContactFilter = s.contactsFilter.filter(c => timelineParticipants.some(p => p.id === c.id))
        contactInContactFilter.forEach(contact => {
          Object.assign(contact, {
            unSeenCount: Math.max(0, contact.unSeenCount - timelineEmailsCount)
          });
        });
      }
    });
    if (!s.requestData.deleted) {
      s.timelineCounts.archiveUnSeenCount -= request.archiveCount;
      s.timelineCounts.unSeenCount -= request.inboxCount;
    }
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.emailMessages.forEach(e => e.seen = true);
        timeline.seen = true;
      }
    })
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  MARK_AS_UNREAD(s, { request, user }) {
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline && !s.requestData.deleted) {
        let timelineParticipants = timeline.participants.filter(p => p.email !== user.email)
        let timelineEmailsCount = timeline.timelineItems.filter(ti => ti.itemType === 1).length
        let contactInContactFilter = s.contactsFilter.filter(c => timelineParticipants.some(p => p.id === c.id))
        contactInContactFilter.forEach(contact => {
          Object.assign(contact, {
            unSeenCount: Math.max(0, contact.unSeenCount + timelineEmailsCount)
          });
        });
      }
    });
    if (!s.requestData.deleted) {
      s.timelineCounts.archiveUnSeenCount += request.archiveCount;
      s.timelineCounts.unSeenCount += request.inboxCount;
    }
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.emailMessages.forEach(e => e.seen = false);
        timeline.seen = false;
      }
    })
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  // Flips a single timeline's read state in place. The `timeline/:id/toggleRead` endpoint owns the
  // real toggle and the action refreshes the counters via `fetchTimelineCount`, so this mutation
  // only mirrors the seen flags the list/thread views render from.
  TOGGLE_READ(s, timelineId) {
    const timeline = s.timelines.items.find(t => t.id === timelineId);
    if (!timeline) return;
    const seen = !timeline.seen;
    timeline.seen = seen;
    (timeline.emailMessages || []).forEach(e => e.seen = seen);
  },
  ERASE_EMAILS(s, ids) {
    s.timelines.items = s.timelines.items.filter(item => !ids.includes(item.id));
    s.timelineCounts.binCount -= ids.length;
    storageHelper.saveTimelineCounts(s.timelineCounts);
  },
  MARK_AS_IMPORTANT(s, request) {
    if (!s.requestData.deleted) {
      s.timelineCounts.importantCount += request.ids.length;
    }
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.isImportant = true;
      }
    })
    storageHelper.saveTimelineCounts(s.timelineCounts);
  },
  MARK_AS_NOT_IMPORTANT(s, request) {
    if (!s.requestData.deleted) {
      s.timelineCounts.importantCount -= request.ids.length;
    }
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.isImportant = false;
      }
    })
    storageHelper.saveTimelineCounts(s.timelineCounts);
  },
  MARK_AS_ARCHIVE(s, { request, user }) {
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.isArchive = true
        let participants = s.contactsFilter
          .filter(c => timeline.participants.some(p => p.id === c.id))
          .filter(c => c.email !== user.email);
        participants.forEach(contact => {
          Object.assign(contact, {
            totalCount: Math.max(0, contact.totalCount - request.ids.length),
            unSeenCount: Math.max(0, contact.unSeenCount - request.unSeenCount)
          });
        });
      }
    });
    if (s.requestData.overdue || s.requestData.waitingOn || s.requestData.important) {
      s.timelineCounts.archiveCount += request.ids.length;
      s.timelineCounts.archiveUnSeenCount += request.unSeenCount;
      s.timelineCounts.totalCount -= request.ids.length;
      s.timelineCounts.unSeenCount -= request.unSeenCount;
    }
    else if (s.requestData.folder === 4) {
      s.timelineCounts.archiveCount += request.ids.length;
      s.timelineCounts.archiveUnSeenCount += request.unSeenCount;
    }
    else if (s.requestData.folder === 2) {
      s.timelineCounts.totalCount -= request.ids.length;
      s.timelineCounts.unSeenCount -= request.unSeenCount;
      s.timelineCounts.archiveCount += request.ids.length;
      s.timelineCounts.archiveUnSeenCount += request.unSeenCount;
      s.timelines.items = s.timelines.items.filter(i => !request.ids.some(id => id === i.id));
    }
    else if (s.requestData.deleted) {
      s.timelineCounts.binCount -= request.ids.length
      s.timelineCounts.archiveCount += request.ids.length;
      s.timelineCounts.archiveUnSeenCount += request.unSeenCount;
      s.timelines.items = s.timelines.items.filter(i => !request.ids.some(id => id === i.id));
    }
    sortContactsFilterByInboxOrder(s);
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  MARK_AS_UNARCHIVE(s, { request, user }) {
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.isArchive = false
        timeline.participants.forEach(participant => {
          if (participant.email === user.email) return;
          let contact = s.contactsFilter.find(c => c.id === participant.id);
          if (!contact) {
            contact = {
              id: participant.id,
              email: participant.email,
              name: participant.name,
              profileColor: participant.profileColor,
              profilePicUrl: participant.profilePicUrl,
              totalCount: 0,
              unSeenCount: 0
            };
            s.contactsFilter.unshift(contact);
          }
          const count = timeline.participants.filter(p => p.id === participant.id).length;
          const unseenCount = timeline.emailMessages
            .filter(email => !email.seen)
            .filter(email => timeline.participants.some(p => p.id === participant.id))
            .length;

          Object.assign(contact, {
            totalCount: Math.max(0, contact.totalCount + count),
            unSeenCount: Math.max(0, contact.unSeenCount + unseenCount)
          });
        });
      }
    });
    if (s.requestData.overdue || s.requestData.waitingOn || s.requestData.important || s.requestData.folder === 4) {
    }
    else {
      s.timelines.items = s.timelines.items.filter(i => !request.ids.some(id => id === i.id));
    }
    if (s.requestData.folder !== 4) {
      s.timelineCounts.totalCount += request.ids.length;
      s.timelineCounts.unSeenCount += request.unSeenCount;
    }
    s.timelineCounts.archiveCount -= request.ids.length;
    s.timelineCounts.archiveUnSeenCount -= request.unSeenCount;
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  MOVE_TO_INBOX(s, { request, user }) {
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        timeline.participants.forEach(participant => {
          if (participant.email === user.email) return;
          let contact = s.contactsFilter.find(c => c.id === participant.id);
          if (!contact) {
            contact = {
              id: participant.id,
              email: participant.email,
              name: participant.name,
              profileColor: participant.profileColor,
              profilePicUrl: participant.profilePicUrl,
              totalCount: 0,
              unSeenCount: 0
            };
            s.contactsFilter.unshift(contact);
          }
          const count = timeline.participants.filter(p => p.id === participant.id).length;
          const unseenCount = timeline.emailMessages
            .filter(email => !email.seen)
            .filter(email => timeline.participants.some(p => p.id === participant.id))
            .length;

          Object.assign(contact, {
            totalCount: Math.max(0, contact.totalCount + count),
            unSeenCount: Math.max(0, contact.unSeenCount + unseenCount)
          });
        });
      }
    });
    s.timelineCounts.totalCount += request.ids.length;
    s.timelineCounts.unSeenCount += request.unSeenCount;
    s.timelineCounts.binCount -= request.ids.length;
    s.timelines.items = s.timelines.items.filter(item => !request.ids.includes(item.id));
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  MOVE_TO_BIN(s, { request, user }) {
    request.ids.forEach(id => {
      const timeline = s.timelines.items.find(t => t.id === id);
      if (timeline) {
        let participants = s.contactsFilter
          .filter(c => timeline.participants.some(p => p.id === c.id))
          .filter(c => c.email !== user.email);
        participants.forEach(contact => {
          Object.assign(contact, {
            totalCount: Math.max(0, contact.totalCount - request.inboxCount),
            unSeenCount: Math.max(0, contact.unSeenCount - request.inboxUnSeenCount)
          });
        });
      }
    });
    s.timelines.items = s.timelines.items.filter(i => !request.ids.some(id => id === i.id));
    s.timelineCounts.binCount += request.ids.length;
    s.timelineCounts.totalCount -= request.inboxCount;
    s.timelineCounts.unSeenCount -= request.inboxUnSeenCount;
    s.timelineCounts.archiveCount -= request.archiveCount;
    s.timelineCounts.archiveUnSeenCount -= request.archiveUnSeenCount;
    s.timelineCounts.importantCount -= request.importantCount;
    s.timelineCounts.waitingOnCount -= request.waitingOnCount;
    if (s.timelineCounts.overdueCount > 0) {
      s.timelineCounts.overdueCount -= request.overdueCount;
    }
    sortContactsFilterByInboxOrder(s);
    storageHelper.saveTimelineCounts(s.timelineCounts);
    storageHelper.saveContactsFilter(s.contactsFilter);
  },
  SET_ISCOLLAPSE_TRUE_All(state) {
    state.timelines.items.forEach((timeline) => {
      timeline.isCollapsed = true;
    });
  },
  ARCHIVE_TIMELINES_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.totalCount -= request.ids.length;
    state.timelineCounts.unSeenCount -= request.unSeenCount;
    state.timelineCounts.archiveCount += request.ids.length;
    state.timelineCounts.archiveUnSeenCount += request.unSeenCount;
    const contact = state.contactsFilter.find(t => t.id === request.contactId);
    if (contact) {
      contact.totalCount -= request.ids.length;
      contact.unSeenCount -= request.unSeenCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  ARCHIVE_TIMELINES_FROM_SIDE_PANEL_ALL(state, request) {
    state.timelineCounts.totalCount -= request.totalCount;
    state.timelineCounts.unSeenCount -= request.unSeenCount;
    state.timelineCounts.archiveCount += request.totalCount;
    state.timelineCounts.archiveUnSeenCount += request.unSeenCount;
    const contact = state.contactsFilter.find(t => t.id === request.id);
    if (contact) {
      contact.totalCount -= request.totalCount;
      contact.unSeenCount -= request.unSeenCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  ARCHIVE_TIMELINES_FROM_CONTACT_FILTER(state, request) {
    let totalCount = 0;
    let unSeenTotal = 0;

    for (const contact of request) {
      totalCount += contact.totalCount;
      unSeenTotal += contact.unSeenCount;
    }

    state.timelineCounts.totalCount -= totalCount;
    state.timelineCounts.unSeenCount -= unSeenTotal;

    state.timelineCounts.archiveCount += totalCount;
    state.timelineCounts.archiveUnSeenCount += unSeenTotal;

    for (const c of request) {
      const contactIndex = state.contactsFilter.findIndex(t => t.id === c.id);
      if (contactIndex === -1) continue;

      const existing = state.contactsFilter[contactIndex];

      existing.totalCount = Math.max(0, existing.totalCount - c.totalCount);
      existing.unSeenCount = Math.max(0, existing.unSeenCount - c.unSeenCount);

      if (existing.totalCount === 0) {
        state.contactsFilter.splice(contactIndex, 1);
      } else {
        state.contactsFilter.splice(contactIndex, 1, { ...existing });
      }
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  DELETE_TIMELINES_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.totalCount -= request.ids.length;
    state.timelineCounts.binCount += request.ids.length;
    state.timelineCounts.unSeenCount -= request.unSeenCount;
    state.timelineCounts.importantCount -= request.importantCount;
    state.timelineCounts.waitingOnCount -= request.waitingOnCount;
    state.timelineCounts.overdueCount -= request.overdueCount;
    const index = state.contactsFilter.findIndex(c => c.id === request.contactId);
    if (index === -1) return;
    const contact = { ...state.contactsFilter[index] };
    contact.totalCount = Math.max(0, contact.totalCount - request.ids.length);
    contact.unSeenCount = Math.max(0, contact.unSeenCount - request.unSeenCount);
    if (contact.totalCount === 0) {
      state.contactsFilter.splice(index, 1);
    } else {
      state.contactsFilter.splice(index, 1, contact);
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  DELETE_TIMELINES_BY_CONTACTID_FROM_SIDE_PANEL(state, { request, contactId }) {
    state.timelineCounts.totalCount -= request.totalCount;
    state.timelineCounts.binCount += request.totalCount;
    state.timelineCounts.unSeenCount -= request.unSeenCount;
    state.timelineCounts.importantCount -= request.importantCount;
    state.timelineCounts.waitingOnCount -= request.waitingOnCount;
    state.timelineCounts.overdueCount -= request.overdueCount;
    const index = state.contactsFilter.findIndex(c => c.id === request.contactId);
    if (index === -1) return;
    const contact = { ...state.contactsFilter[index] };
    contact.totalCount = Math.max(0, contact.totalCount - request.totalCount);
    contact.unSeenCount = Math.max(0, contact.unSeenCount - request.unSeenCount);
    if (contact.totalCount === 0) {
      state.contactsFilter.splice(index, 1);
    } else {
      state.contactsFilter.splice(index, 1, contact);
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  DELETE_TIMELINES_BY_CONTACTIDS_FROM_SIDE_PANEL(state, { request, contactIds }) {
    // ---- Update global timeline counters (safe) ----
    const c = state.timelineCounts;

    c.totalCount = Math.max(0, c.totalCount - request.totalCount);
    c.binCount += request.totalCount;
    c.unSeenCount = Math.max(0, c.unSeenCount - request.unSeenCount);
    c.importantCount = Math.max(0, c.importantCount - request.importantCount);
    c.waitingOnCount = Math.max(0, c.waitingOnCount - request.waitingOnCount);
    c.overdueCount = Math.max(0, c.overdueCount - request.overdueCount);

    // ---- For EACH contact ID, update/remove contact ----
    contactIds.forEach(contactId => {
      const index = state.contactsFilter.findIndex(c => c.id === contactId);
      if (index === -1) return;

      const contact = { ...state.contactsFilter[index] };

      // Reduce the counts safely
      contact.totalCount = Math.max(0, contact.totalCount - request.totalCount);
      contact.unSeenCount = Math.max(0, contact.unSeenCount - request.unSeenCount);

      // Remove or update
      if (contact.totalCount === 0) {
        state.contactsFilter.splice(index, 1);
      } else {
        state.contactsFilter.splice(index, 1, contact);
      }
    });

    // ---- Save changes ----
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  DELETE_TIMELINES_ALL(state, { request, contactId }) {
    state.timelineCounts.totalCount -= request.totalCount;
    state.timelineCounts.binCount += request.totalCount;
    state.timelineCounts.unSeenCount -= request.unSeenCount;
    state.timelineCounts.importantCount -= request.importantCount;
    state.timelineCounts.waitingOnCount -= request.waitingOnCount;
    state.timelineCounts.overdueCount -= request.overdueCount;
    const contact = state.contactsFilter.find(t => t.id === contactId);
    if (contact) {
      contact.totalCount -= request.totalCount;
      contact.unSeenCount -= request.unSeenCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  MARK_AS_IMPORTANT_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.importantCount += request.ids.length;
    storageHelper.saveTimelineCounts(state.timelineCounts);
  },
  ALL_IMPORTANT(state, count) {
    state.timelineCounts.importantCount += count;
    storageHelper.saveTimelineCounts(state.timelineCounts);
  },
  ALL_NOT_IMPORTANT(state, count) {
    state.timelineCounts.importantCount -= count;
    storageHelper.saveTimelineCounts(state.timelineCounts);
  },
  MARK_AS_NOT_IMPORTANT_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.importantCount -= request.ids.length;
    storageHelper.saveTimelineCounts(state.timelineCounts);
  },
  MARK_AS_READ_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.unSeenCount -= request.inboxCount;
    const contact = state.contactsFilter.find(t => t.id === request.contactId);
    if (contact) {
      contact.unSeenCount -= request.inboxCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  MARK_ALL_AS_READ_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.unSeenCount -= request.unSeenCount;
    const contact = state.contactsFilter.find(t => t.id === request.contactId);
    if (contact) {
      contact.unSeenCount -= request.unSeenCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  MARK_ALL_AS_UNREAD_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.unSeenCount += request.seenCount;
    const contact = state.contactsFilter.find(t => t.id === request.contactId);
    if (contact) {
      contact.unSeenCount += request.seenCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  MARK_AS_UNREAD_FROM_SIDE_PANEL(state, request) {
    state.timelineCounts.unSeenCount += request.inboxCount;
    const contact = state.contactsFilter.find(t => t.id === request.contactId);
    if (contact) {
      contact.unSeenCount += request.inboxCount;
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
    storageHelper.saveContactsFilter(state.contactsFilter);
  },
  TOGGLE_WAITINGON_TIMELINES_FROM_SIDE_PANEL(state, request) {
    const { addWaitingOn, addOverDue, removeOverDue } = request;
    if (state.timelineCounts) {
      state.timelineCounts.waitingOnCount = Math.max(0, state.timelineCounts.waitingOnCount + (addWaitingOn ? 1 : -1));
      if (addOverDue) {
        state.timelineCounts.overdueCount = Math.max(0, state.timelineCounts.overdueCount + 1);
      } else if (removeOverDue) {
        state.timelineCounts.overdueCount = Math.max(0, state.timelineCounts.overdueCount - 1);
      }
    }
    storageHelper.saveTimelineCounts(state.timelineCounts);
  },
  UPDATE_WAITING(s, model) {
    const timeline = s.timelines.items.find(t => t.id === model.request.timelineId);
    if (timeline) {
      timeline.waitingOn = !timeline.waitingOn;
      timeline.dueDate = model.request.dueDate
    }
    if (!s.requestData.deleted) {
      if (s.timelineCounts) {
        s.timelineCounts.waitingOnCount = Math.max(0, s.timelineCounts.waitingOnCount + (model.calculatedRequest.addWaitingOn ? 1 : -1));
        if (model.calculatedRequest.addOverDue) {
          s.timelineCounts.overdueCount = Math.max(0, s.timelineCounts.overdueCount + 1);
        } else if (model.calculatedRequest.removeOverDue) {
          s.timelineCounts.overdueCount = Math.max(0, s.timelineCounts.overdueCount - 1);
        }
      }
    }
    storageHelper.saveTimelineCounts(s.timelineCounts);
  },
  SET_TIMELINE_REF(state, { id, el }) {
    if (el) state.timelineRefs[id] = el;
  }
}
export default {
  state,
  getters,
  actions,
  mutations
};